import { defineStore } from 'pinia'
import type { User } from '@supabase/supabase-js'
import type { UserProfile, RestauranteWithRole } from '~/types/database'
import { pickCurrentRestaurant } from '~/utils/pickCurrentRestaurant'

const STORAGE_KEY = 'comandaplus'

export const useAuthStore = defineStore('auth', () => {
  const client = useSupabaseClient()
  const { handleError } = useSupabaseHelpers()

  // Estado
  const user = ref<User | null>(null)
  const profile = ref<UserProfile | null>(null)
  const userRestaurants = ref<RestauranteWithRole[]>([])
  const currentRestaurant = ref<RestauranteWithRole | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  // Flags internos para evitar race conditions
  let _isSigningIn = false
  let _initializePromise: Promise<void> | null = null

  // Computed
  const isAuthenticated = computed(() => !!user.value)
  const userRole = computed(() => profile.value?.rol || 'cliente')
  const hasProfile = computed(() => !!profile.value)
  const isDueño = computed(() => profile.value?.rol === 'dueño')
  const isEncargado = computed(() => profile.value?.rol === 'encargado')
  const isEmpleado = computed(() => profile.value?.rol === 'empleado')
  const isAdmin = computed(() => profile.value?.rol === 'admin')
  const canAccessDashboard = computed(() =>
    isDueño.value || isEncargado.value || isAdmin.value || isEmpleado.value
  )
  const isCurrentRestaurantOwner = computed(() =>
    currentRestaurant.value?.propietario_id === profile.value?.id
  )
  const currentRestaurantRole = computed(() => currentRestaurant.value?.role)
  const currentRestaurantPermissions = computed(() => {
    if (!currentRestaurant.value) return {}
    const role = currentRestaurant.value.role
    return {
      canManageMenu: role === 'dueño' || role === 'encargado',
      canManageEmployees: role === 'dueño',
      canViewAnalytics: role === 'dueño' || role === 'encargado',
      canManageOrders: true,
      canViewReports: role === 'dueño' || role === 'encargado'
    }
  })

  // Promise que se resuelve cuando el perfil y restaurantes terminan de cargar
  let profileReadyResolve: (() => void) | null = null
  let profileReadyPromise: Promise<void> | null = null

  // Watcher: cuando cambia el user, cargar perfil y restaurantes
  // flush: 'sync' asegura que profileReadyPromise se cree inmediatamente
  // al cambiar user.value (antes de que waitForProfileReady() lo lea)
  watch(user, async (newUser) => {
    // Crear una nueva promise para que signIn pueda esperar
    // Guardar referencia local al resolve para evitar que re-entradas lo sobreescriban
    let localResolve: (() => void) | null = null
    profileReadyPromise = new Promise(resolve => {
      localResolve = resolve
      profileReadyResolve = resolve
    })

    if (newUser) {
      await loadUserProfile(newUser.id)
      if (profile.value) {
        // loadUserRestaurants ya aplica pickCurrentRestaurant con la selección
        // guardada — no llamar a loadCurrentRestaurantFromStorage aquí porque
        // antes ese segundo paso rompía la regla multi-tenant (bug C2).
        await loadUserRestaurants(profile.value.id)
      } else {
        userRestaurants.value = []
        setCurrentRestaurant(null)
      }
    } else {
      profile.value = null
      userRestaurants.value = []
      setCurrentRestaurant(null)
    }

    // Señalar que terminó la carga (usar referencia local por seguridad)
    if (localResolve) localResolve()
  }, { flush: 'sync' })

  /** Espera a que el perfil y restaurantes terminen de cargar tras un cambio de usuario.
   *  Devuelve `true` si terminó correctamente, `false` si llegó al timeout (10s).
   *  Antes resolvía silenciosamente en timeout y los callers no podían distinguir
   *  un "perfil cargado OK" de un "timeout aplastado" → race conditions en redirects.
   *  Fix REGISTER-#6. */
  async function waitForProfileReady(): Promise<boolean> {
    if (!profileReadyPromise) return true
    let timedOut = false
    const timeout = new Promise<void>(resolve => setTimeout(() => {
      timedOut = true
      resolve()
    }, 10000))
    await Promise.race([profileReadyPromise, timeout])
    if (timedOut) {
      console.warn('[auth] waitForProfileReady: timeout 10s — el perfil no terminó de cargar')
    }
    return !timedOut
  }

  async function initialize() {
    if (isInitialized.value) return
    try {
      loading.value = true
      error.value = null

      // Errores recuperables: sesión ausente o refresh token inválido/expirado
      const isRecoverableAuthError = (msg: string) =>
        msg === 'Auth session missing!' ||
        msg.includes('Refresh Token') ||
        msg.includes('refresh_token') ||
        msg.includes('Invalid Refresh Token') ||
        msg.includes('JWT expired')

      // Usar getUser() en vez de getSession() para validar el JWT contra el servidor
      const { data: { user: authUser }, error: userError } = await client.auth.getUser()

      if (userError) {
        if (isRecoverableAuthError(userError.message)) {
          // Limpiar la sesión corrupta/expirada del almacenamiento (solo en cliente)
          if (import.meta.client) {
            await client.auth.signOut({ scope: 'local' }).catch(() => {})
            // Limpieza defensiva: el módulo @nuxtjs/supabase a veces deja
            // restos en localStorage tras un refresh fallido. Borrar TODAS
            // las claves sb-* garantiza que el siguiente login arranque limpio
            // y evita el bucle "Invalid Refresh Token" en cargas siguientes.
            try {
              Object.keys(localStorage)
                .filter(k => k.startsWith('sb-'))
                .forEach(k => localStorage.removeItem(k))
            } catch {} // noop si localStorage no disponible (Safari ITP, etc.)
          }
          user.value = null
        } else {
          throw userError
        }
      } else {
        user.value = authUser || null
      }

      // Registrar listener de cambios de sesión (solo en cliente — en SSR no hay estado persistente)
      if (import.meta.client) {
        client.auth.onAuthStateChange(async (_event, newSession) => {
          // NO interferir si hay un signIn activo — signIn maneja user.value directamente
          if (_isSigningIn) return

          if (newSession) {
            // Solo actualizar si el usuario realmente cambió (ej: otra pestaña)
            const newUserId = newSession.user?.id
            if (newUserId && user.value?.id !== newUserId) {
              user.value = newSession.user
            } else if (!newUserId && user.value) {
              user.value = null
            }
          } else {
            if (user.value) user.value = null
          }
        })
      }
      isInitialized.value = true
    } catch (err: any) {
      error.value = handleError(err).message
      // Aunque falle, marcar como inicializado para no bloquear la app
      isInitialized.value = true
    } finally {
      loading.value = false
    }
  }

  async function loadUserProfile(authUserId: string) {
    try {
      const { data, error: profileError } = await client
        .from('perfiles').select('*').eq('auth_user_id', authUserId).single()
      if (profileError && profileError.code !== 'PGRST116') throw profileError
      profile.value = data || null
    } catch (err: any) {
      error.value = handleError(err).message
      profile.value = null
    }
  }

  async function loadUserRestaurants(perfilId: string) {
    if (!profile.value?.id) {
      userRestaurants.value = []
      setCurrentRestaurant(null)
      return
    }
    try {
      loading.value = true
      const loaded: RestauranteWithRole[] = []

      // Restaurantes propios (propietario_id = profile.id)
      const { data: owned, error: ownedError } = await client
        .from('restaurantes').select('*')
        .eq('propietario_id', profile.value.id)
        .eq('esta_activo', true)
      if (ownedError) throw ownedError
      if (owned) {
        loaded.push(...owned.map(r => ({ ...r, role: 'dueño' as const })))
      }

      // Restaurantes asignados como empleado
      const { data: assigned, error: assignedError } = await client
        .from('empleados').select('posicion, restaurantes!inner (*)')
        .eq('perfil_id', perfilId)
        .eq('esta_activo', true)
        .eq('restaurantes.esta_activo', true)
      if (assignedError) throw assignedError
      if (assigned) {
        assigned.forEach((a: any) => {
          const rd = a.restaurantes
          if (rd && !loaded.some(lr => lr.id === rd.id)) {
            loaded.push({
              ...rd,
              role: (a.posicion === 'encargado' || a.posicion === 'encargada' ? 'encargado' : 'empleado') as 'encargado' | 'empleado',
              posicion: a.posicion
            })
          }
        })
      }

      loaded.sort((a, b) => a.nombre.localeCompare(b.nombre))
      userRestaurants.value = loaded

      // Intentar restaurar el restaurante guardado
      // Regla: si hay 2+ restaurantes y NO hay selección previa válida, dejar
      // currentRestaurant=null para que el middleware redirija a /elegir-restaurante.
      // Misma regla en SSR (sin storage) y cliente — ver utils/pickCurrentRestaurant.ts.
      const savedId = import.meta.client
        ? localStorage.getItem(`${STORAGE_KEY}_currentRestaurantId`)
        : null
      setCurrentRestaurant(pickCurrentRestaurant(loaded, savedId))
    } catch (err: any) {
      error.value = handleError(err).message
      userRestaurants.value = []
      setCurrentRestaurant(null)
    } finally {
      loading.value = false
    }
  }

  function setCurrentRestaurant(restaurantId: string | null) {
    if (restaurantId === null) {
      currentRestaurant.value = null
      if (import.meta.client) {
        localStorage.removeItem(`${STORAGE_KEY}_currentRestaurantId`)
      }
      return
    }
    const found = userRestaurants.value.find(r => r.id === restaurantId)
    if (found) {
      currentRestaurant.value = found
      if (import.meta.client) {
        localStorage.setItem(`${STORAGE_KEY}_currentRestaurantId`, restaurantId)
      }
    } else {
      currentRestaurant.value = null
      if (import.meta.client) {
        localStorage.removeItem(`${STORAGE_KEY}_currentRestaurantId`)
      }
    }
  }

  function loadCurrentRestaurantFromStorage() {
    if (!import.meta.client) return
    const savedId = localStorage.getItem(`${STORAGE_KEY}_currentRestaurantId`)
    // Fix C2 — antes auto-picaba userRestaurants[0] cuando había 2+ sin selección,
    // saltando el picker /elegir-restaurante. La regla correcta vive en
    // utils/pickCurrentRestaurant.ts y está cubierta por test unitario.
    setCurrentRestaurant(pickCurrentRestaurant(userRestaurants.value, savedId))
  }

  async function createProfile(profileData: {
    nombre_completo: string
    telefono?: string
    rol?: UserProfile['rol']
  }) {
    if (!user.value) throw new Error('No hay usuario autenticado')
    try {
      loading.value = true
      error.value = null

      // Fix REGISTER-#1 — antes usaba upsert con onConflict que silenciaba duplicados:
      // si el usuario enviaba el form 2 veces, la 2ª sobreescribía la 1ª sin aviso.
      // Ahora: si ya existe perfil para este auth_user_id, lo CARGAMOS (idempotencia
      // segura para reentrada legítima tras refresh) en lugar de sobreescribir.
      const { data: existente } = await client
        .from('perfiles')
        .select('*')
        .eq('auth_user_id', user.value.id)
        .maybeSingle()

      if (existente) {
        profile.value = existente
        await loadUserRestaurants(existente.id)
        return existente
      }

      const { data, error: createError } = await client
        .from('perfiles')
        .insert({
          auth_user_id: user.value.id,
          email: user.value.email!,
          nombre_completo: profileData.nombre_completo,
          telefono: profileData.telefono || undefined,
          rol: profileData.rol || 'cliente',
          esta_activo: true,
        })
        .select()
        .single()

      // Race-condition fallback: si entre el SELECT y el INSERT alguien creó
      // el perfil (ej. el usuario abrió 2 pestañas), 23505 = unique_violation.
      // Recuperamos el row existente en lugar de fallar.
      if (createError) {
        if ((createError as any).code === '23505') {
          const { data: row } = await client
            .from('perfiles')
            .select('*')
            .eq('auth_user_id', user.value.id)
            .maybeSingle()
          if (row) {
            profile.value = row
            await loadUserRestaurants(row.id)
            return row
          }
        }
        throw createError
      }

      profile.value = data
      if (data) await loadUserRestaurants(data.id)
      return data
    } catch (err: any) {
      error.value = handleError(err).message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateProfile(updates: Partial<UserProfile>) {
    if (!profile.value) throw new Error('No hay perfil para actualizar')
    try {
      loading.value = true
      error.value = null
      const { data, error: updateError } = await client
        .from('perfiles').update(updates)
        .eq('id', profile.value.id).select().single()
      if (updateError) throw updateError
      profile.value = data
      return data
    } catch (err: any) {
      error.value = handleError(err).message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function signIn(email: string, password: string) {
    try {
      loading.value = true
      error.value = null

      // Esperar a que initialize() termine para evitar race conditions
      // (initialize podría llamar signOut y limpiar nuestra sesión nueva)
      if (_initializePromise) await _initializePromise

      _isSigningIn = true
      const { data, error: signInError } = await client.auth.signInWithPassword({ email, password })
      if (signInError) throw signInError
      // Establecer user.value directamente para disparar el watcher de inmediato
      // (no depender de onAuthStateChange que es asíncrono)
      user.value = data.user
      return { user: data.user, session: data.session }
    } catch (err: any) {
      error.value = handleError(err).message
      throw err
    } finally {
      _isSigningIn = false
      loading.value = false
    }
  }

  async function signUp(email: string, password: string, additionalData?: { full_name?: string; phone?: string }) {
    try {
      loading.value = true
      error.value = null
      const { data, error: signUpError } = await client.auth.signUp({
        email,
        password,
        options: {
          data: additionalData,
          emailRedirectTo: `${window.location.origin}/auth/confirm`
        }
      })
      if (signUpError) throw signUpError
      // Establecer user.value si la sesión se creó (sin verificación de email)
      if (data.user) user.value = data.user
      return data
    } catch (err: any) {
      error.value = handleError(err).message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function signOut() {
    try {
      loading.value = true
      error.value = null
      const { error: signOutError } = await client.auth.signOut()
      if (signOutError) throw signOutError
    } catch (err: any) {
      error.value = handleError(err).message
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(email: string) {
    try {
      loading.value = true
      error.value = null
      // El enlace debe llevar al usuario a una página donde fija la contraseña
      // nueva, NO a /auth/confirm (que solo verifica email y mete en dashboard
      // sin pedir contraseña). Bug histórico: la función "recuperar contraseña"
      // era decorativa porque el redirect iba al sitio equivocado.
      const { error: resetError } = await client.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      })
      if (resetError) throw resetError
    } catch (err: any) {
      error.value = handleError(err).message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function checkRestaurantAccess(restaurantId: string): Promise<boolean> {
    if (!profile.value?.id) return false
    try {
      const { data: ownerRestaurant } = await client
        .from('restaurantes').select('id')
        .eq('id', restaurantId)
        .eq('propietario_id', profile.value.id)
        .maybeSingle()
      if (ownerRestaurant) return true

      const { data: employee } = await client
        .from('empleados').select('id')
        .eq('restaurante_id', restaurantId)
        .eq('perfil_id', profile.value.id)
        .eq('esta_activo', true)
        .maybeSingle()
      if (employee) return true

      return false
    } catch {
      return false
    }
  }

  function clearError() {
    error.value = null
  }

  // Inicializar automáticamente (guardar la promise para que signIn pueda esperarla)
  _initializePromise = initialize().finally(() => { _initializePromise = null })

  return {
    // Estado (no usar readonly() en Pinia setup stores — causa conflictos de reactividad)
    user,
    profile,
    userRestaurants,
    currentRestaurant,
    loading,
    error,
    isInitialized,
    // Computed
    isAuthenticated,
    userRole,
    hasProfile,
    isDueño,
    isEncargado,
    isEmpleado,
    isAdmin,
    canAccessDashboard,
    isCurrentRestaurantOwner,
    currentRestaurantRole,
    currentRestaurantPermissions,
    // Métodos
    initialize,
    loadUserRestaurants,
    setCurrentRestaurant,
    createProfile,
    updateProfile,
    signIn,
    signUp,
    signOut,
    resetPassword,
    checkRestaurantAccess,
    clearError,
    waitForProfileReady
  }
})
